import { FiFolder } from 'react-icons/fi';

export const topicVocabularyData = {
  courseId: 'topic-vocabulary',
  courseType: 'vocabulary',
  title: 'Topic-Related Vocabulary',
  icon: FiFolder,
  overview: {
    impact: 'Be prepared for any IELTS topic',
    focus: 'Vocabulary organized by common topics',
    uses: 'Writing Task 2, Speaking',
    duration: '30 minutes',
  },
  sections: [
    {
      title: 'Why Topic Vocabulary Matters',
      content: `<p>IELTS tests cover predictable topics. Having ready vocabulary for each topic improves your performance.</p>`,
      points: [
        'Respond quickly in Speaking with relevant words',
        'Write essays with topic-specific vocabulary',
        'Understand Reading and Listening passages better',
      ],
    },
    {
      title: 'Environment',
      content: `<p>Key vocabulary for environment topics:</p>`,
      table: {
        headers: ['Word', 'Type', 'Example Sentence'],
        rows: [
          ['sustainable', 'adj', 'We need sustainable energy solutions.'],
          ['carbon footprint', 'noun', 'Reducing your carbon footprint helps the planet.'],
          ['biodiversity', 'noun', 'Biodiversity is declining rapidly.'],
          ['renewable', 'adj', 'Solar is a renewable energy source.'],
          ['conservation', 'noun', 'Wildlife conservation is essential.'],
        ],
      },
    },
    {
      title: 'Technology',
      content: `<p>Key vocabulary for technology topics:</p>`,
      table: {
        headers: ['Word', 'Type', 'Example Sentence'],
        rows: [
          ['innovation', 'noun', 'Innovation drives economic growth.'],
          ['digital', 'adj', 'The digital revolution has transformed society.'],
          ['artificial intelligence', 'noun', 'AI will change the job market.'],
          ['automated', 'adj', 'Automated systems increase efficiency.'],
          ['cybersecurity', 'noun', 'Cybersecurity threats are increasing.'],
        ],
      },
    },
    {
      title: 'Education',
      content: `<p>Key vocabulary for education topics:</p>`,
      table: {
        headers: ['Word', 'Type', 'Example Sentence'],
        rows: [
          ['curriculum', 'noun', 'The school curriculum needs updating.'],
          ['vocational', 'adj', 'Vocational training prepares students for jobs.'],
          ['literacy', 'noun', 'Literacy rates have improved globally.'],
          ['tertiary', 'adj', 'Tertiary education should be accessible.'],
          ['pedagogy', 'noun', 'Modern pedagogy focuses on critical thinking.'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Match Topic Vocabulary',
      type: 'select',
      instructions: 'Which topic does each word belong to?',
      questions: [
        {
          text: 'biodiversity',
          options: ['Environment', 'Technology', 'Education'],
          answer: 'Environment',
          explanation: 'Biodiversity relates to the variety of life on Earth.',
        },
        {
          text: 'curriculum',
          options: ['Education', 'Environment', 'Health'],
          answer: 'Education',
          explanation: 'Curriculum refers to school subjects and content.',
        },
        {
          text: 'automation',
          options: ['Technology', 'Education', 'Environment'],
          answer: 'Technology',
          explanation: 'Automation is the use of technology to perform tasks.',
        },
        {
          text: 'renewable',
          options: ['Environment', 'Technology', 'Education'],
          answer: 'Environment',
          explanation: 'Renewable relates to energy sources that can be replenished.',
        },
        {
          text: 'pedagogy',
          options: ['Education', 'Technology', 'Health'],
          answer: 'Education',
          explanation: 'Pedagogy is the theory and practice of teaching.',
        },
        {
          text: 'cybersecurity',
          options: ['Technology', 'Environment', 'Education'],
          answer: 'Technology',
          explanation: 'Cybersecurity protects digital systems from attacks.',
        },
      ],
    },
    {
      title: 'Complete Environment Sentences',
      type: 'fill-in',
      instructions: 'Type the correct environment vocabulary word.',
      questions: [
        {
          text: 'We need to reduce our carbon ___ to fight climate change.',
          answer: 'footprint',
          explanation: '"Carbon footprint" = the amount of CO2 produced by activities.',
        },
        {
          text: 'Solar and wind are examples of ___ energy.',
          answer: 'renewable',
          explanation: '"Renewable energy" can be naturally replenished.',
        },
        {
          text: 'The loss of ___ threatens many ecosystems.',
          answer: 'biodiversity',
          explanation: '"Biodiversity" = variety of plant and animal life.',
        },
      ],
    },
    {
      title: 'Complete Technology Sentences',
      type: 'fill-in',
      instructions: 'Type the correct technology vocabulary word.',
      questions: [
        {
          text: 'Artificial ___ is transforming many industries.',
          answer: 'intelligence',
          explanation: '"Artificial intelligence (AI)" = machines that learn and reason.',
        },
        {
          text: 'Many jobs may be replaced by ___.',
          answer: 'automation',
          explanation: '"Automation" = using technology to perform tasks without human input.',
        },
        {
          text: 'Technological ___ drives economic growth.',
          answer: 'innovation',
          explanation: '"Innovation" = new ideas, methods, or products.',
        },
      ],
    },
    {
      title: 'Use Topic Words in Context',
      type: 'select',
      instructions: 'Choose the correct word to complete each sentence.',
      questions: [
        {
          text: 'Many countries are investing in ___ energy sources.',
          options: ['sustainable', 'digital', 'tertiary'],
          answer: 'sustainable',
          explanation: '"Sustainable energy" can meet current needs without compromising the future.',
        },
        {
          text: 'The school ___ should include more practical subjects.',
          options: ['curriculum', 'conservation', 'automation'],
          answer: 'curriculum',
          explanation: '"Curriculum" = the subjects and content taught in schools.',
        },
        {
          text: '___ training prepares students for specific careers.',
          options: ['Vocational', 'Digital', 'Renewable'],
          answer: 'Vocational',
          explanation: '"Vocational training" = job-specific education.',
        },
      ],
    },
    {
      title: 'Write Topic Sentences',
      type: 'writing',
      instructions: 'Write a sentence using each topic vocabulary word.',
      questions: [
        {
          text: 'Write a sentence about "carbon footprint"',
          example: 'Governments should implement policies to help citizens reduce their carbon footprint.',
          tip: 'Carbon footprint = the total greenhouse gases produced by our actions.',
        },
        {
          text: 'Write a sentence about "artificial intelligence"',
          example: 'Artificial intelligence is increasingly being used to diagnose medical conditions.',
          tip: 'AI = computer systems that can learn and make decisions.',
        },
        {
          text: 'Write a sentence about "literacy"',
          example: 'Improving literacy rates is essential for economic development in developing countries.',
          tip: 'Literacy = the ability to read and write.',
        },
      ],
    },
  ],
  tips: [
    'Create vocabulary lists for each IELTS topic',
    'Learn collocations within each topic',
    'Practice essays on different topics weekly',
    'Read articles on IELTS topics to expand vocabulary',
  ],
  dailyPractice: [
    'Study vocabulary for one topic per day',
    'Write a paragraph using topic vocabulary',
    'Practice speaking about different topics',
    'Review and test yourself on topic words',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Topic-Related Vocabulary: Organized vocabulary for common IELTS topics including environment, technology, education, health, and society.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...topicVocabularyData,
      title: topicVocabularyData.title + ' (Foundations)',
      overview: {
        ...topicVocabularyData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: topicVocabularyData.sections.slice(0, Math.max(2, Math.ceil(topicVocabularyData.sections.length / 2))),
      exercises: topicVocabularyData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...topicVocabularyData,
      title: topicVocabularyData.title + ' (Advanced)',
      overview: {
        ...topicVocabularyData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: topicVocabularyData.sections,
      exercises: topicVocabularyData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(topicVocabularyData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return topicVocabularyData;
}
